import { useEffect, useState, type ReactNode } from "react";
import { Outlet, useLocation } from "react-router-dom";
import { cn } from "@/lib/utils";
import { Sheet, SheetClose, SheetContent } from "@/components/ui/sheet";
import { NavLink } from "@/components/NavLink";
import { Button } from "@/components/ui/button";
import { ChevronDown, LogOut, X } from "lucide-react";
import { useLogout } from "@/hooks/useLogout";
import {
  Collapsible,
  CollapsibleContent,
  CollapsibleTrigger,
} from "@/components/ui/collapsible";
import PanelSidebar, { type PanelBrand, type PanelNavEntry } from "./PanelSidebar";
import PanelTopbar from "./PanelTopbar";

function isActiveRoute(pathname: string, url: string) {
  const segments = url.split("/").filter(Boolean);
  if (segments.length <= 1) return pathname === url;
  return pathname === url || pathname.startsWith(`${url}/`);
}

export interface PanelLayoutProps {
  brand: PanelBrand;
  navItems: PanelNavEntry[];
  footer: { label: string; url: string };
  breadcrumbPrefix: string;
  pageTitle: string;
  sectionLabel?: string;
  topbarActions?: ReactNode;
}

const PanelLayout = ({
  brand,
  navItems,
  footer,
  breadcrumbPrefix,
  pageTitle,
  sectionLabel,
  topbarActions,
}: PanelLayoutProps) => {
  const location = useLocation();
  const [collapsed, setCollapsed] = useState(false);
  const [mobileOpen, setMobileOpen] = useState(false);
  const [mobileOpenGroups, setMobileOpenGroups] = useState<Record<string, boolean>>({});
  const logout = useLogout();
  const BrandIcon = brand.icon;

  useEffect(() => {
    setMobileOpen(false);
  }, [location.pathname]);

  useEffect(() => {
    const mediaQuery = window.matchMedia("(min-width: 1024px)");
    const handleChange = () => {
      if (mediaQuery.matches) {
        setMobileOpen(false);
      }
    };

    handleChange();
    mediaQuery.addEventListener("change", handleChange);
    return () => mediaQuery.removeEventListener("change", handleChange);
  }, []);

  useEffect(() => {
    navItems.forEach((entry) => {
      if (entry.type === "group") {
        const groupActive = entry.children.some((child) => isActiveRoute(location.pathname, child.url));
        if (groupActive) {
          setMobileOpenGroups((prev) => ({ ...prev, [entry.title]: true }));
        }
      }
    });
  }, [location.pathname, navItems]);

  return (
    <div className="min-h-screen bg-background dark:bg-black">
      <div className="hidden lg:block">
        <PanelSidebar
          collapsed={collapsed}
          brand={brand}
          navItems={navItems}
          footer={footer}
          sectionLabel={sectionLabel}
        />
      </div>

      <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
        <SheetContent side="left" className="w-[232px] p-0 bg-black border-r border-neutral-800 [&>button]:hidden">
          <div className="flex h-16 items-center justify-between border-b border-neutral-800 px-4">
            <div className="flex items-center gap-2.5 min-w-0">
              <div className={cn("flex h-9 w-9 shrink-0 items-center justify-center rounded-xl shadow-sm", brand.iconClassName ?? "bg-gradient-primary")}>
                <BrandIcon className={cn("h-5 w-5", brand.iconIconClassName ?? "text-primary-foreground")} />
              </div>
              <div className="min-w-0 leading-tight">
                <span className="block truncate font-heading text-sm font-bold text-white tracking-tight">{brand.title}</span>
                {brand.subtitle ? (
                  <span className="block truncate text-[10px] font-medium text-neutral-500">{brand.subtitle}</span>
                ) : null}
              </div>
            </div>
            <SheetClose asChild>
              <Button
                variant="ghost"
                size="icon"
                className="h-9 w-9 shrink-0 rounded-lg text-neutral-400 hover:bg-white/10 hover:text-white"
                aria-label="Close menu"
              >
                <X className="h-5 w-5" />
              </Button>
            </SheetClose>
          </div>

          <nav className="scrollbar-hover overflow-y-auto px-2.5 py-4 space-y-0.5 h-[calc(100vh-4rem-4rem)]">
            <p className="px-3 pb-1.5 text-[10px] font-bold uppercase tracking-[0.15em] text-neutral-500">
              {sectionLabel ?? "MENU"}
            </p>
            {navItems.map((entry) => {
              if (entry.type === "group") {
                const groupActive = entry.children.some((child) =>
                  isActiveRoute(location.pathname, child.url),
                );
                const isGroupOpen = mobileOpenGroups[entry.title] ?? groupActive;

                return (
                  <Collapsible
                    key={entry.title}
                    open={isGroupOpen}
                    onOpenChange={(open) =>
                      setMobileOpenGroups((prev) => ({ ...prev, [entry.title]: open }))
                    }
                    className="space-y-0.5"
                  >
                    <CollapsibleTrigger
                      className={cn(
                        "flex w-full items-center gap-3 rounded-lg px-3 py-2 text-[13px] font-medium transition-all duration-200",
                        "text-neutral-400 hover:bg-white/10 hover:text-white",
                        groupActive && "text-white",
                      )}
                    >
                      <entry.icon className="h-[18px] w-[18px] shrink-0" />
                      <span className="flex-1 truncate text-left">{entry.title}</span>
                      <ChevronDown
                        className={cn(
                          "h-4 w-4 shrink-0 transition-transform duration-300 ease-in-out",
                          isGroupOpen && "rotate-180",
                        )}
                      />
                    </CollapsibleTrigger>
                    <CollapsibleContent className="space-y-0.5 pt-0.5">
                      {entry.children.map((child) => (
                        <NavLink
                          key={child.url}
                          to={child.url}
                          className="flex items-center gap-3 rounded-lg py-2 pl-9 pr-3 text-[13px] font-medium text-neutral-400 transition-all hover:bg-white/10 hover:text-white"
                          activeClassName="bg-gradient-primary text-primary-foreground shadow-md"
                          onClick={() => setMobileOpen(false)}
                        >
                          <span className="h-1.5 w-1.5 shrink-0 rounded-full bg-neutral-500" />
                          <span>{child.title}</span>
                        </NavLink>
                      ))}
                    </CollapsibleContent>
                  </Collapsible>
                );
              }

              return (
                <NavLink
                  key={entry.title}
                  to={entry.url}
                  end={entry.url.split("/").filter(Boolean).length <= 1}
                  className="flex items-center justify-between rounded-lg px-3 py-2.5 text-[13px] font-medium text-neutral-400 transition-all hover:bg-white/10 hover:text-white"
                  activeClassName="bg-gradient-primary text-primary-foreground shadow-md"
                  onClick={() => setMobileOpen(false)}
                >
                  <div className="flex items-center gap-3">
                    <entry.icon className="h-[18px] w-[18px]" />
                    <span>{entry.title}</span>
                  </div>
                  {entry.badge ? (
                    <span className="flex h-5 min-w-5 items-center justify-center rounded-full bg-destructive px-1.5 text-[10px] font-bold text-destructive-foreground">
                      {entry.badge}
                    </span>
                  ) : null}
                </NavLink>
              );
            })}
          </nav>

          <div className="absolute bottom-0 left-0 right-0 border-t border-neutral-800 p-2.5">
            <button
              type="button"
              onClick={() => {
                setMobileOpen(false);
                logout();
              }}
              className="flex w-full items-center gap-3 rounded-lg px-3 py-2.5 text-[13px] font-medium text-neutral-400 transition-all hover:bg-red-500/10 hover:text-red-400"
            >
              <LogOut className="h-[18px] w-[18px]" />
              <span>{footer.label}</span>
            </button>
          </div>
        </SheetContent>
      </Sheet>

      <div
        className={cn(
          "flex min-h-screen min-w-0 flex-col transition-all duration-300 ease-[cubic-bezier(0.4,0,0.2,1)]",
          collapsed ? "lg:ml-[68px]" : "lg:ml-[216px]"
        )}
      >
        <PanelTopbar
          collapsed={collapsed}
          mobileMenuOpen={mobileOpen}
          onToggleSidebar={() => setCollapsed(!collapsed)}
          onMobileMenuToggle={() => setMobileOpen((open) => !open)}
          breadcrumbPrefix={breadcrumbPrefix}
          pageTitle={pageTitle}
          actions={topbarActions}
        />

        <main className="flex-1 overflow-x-clip p-3 sm:p-4 lg:p-6 xl:p-8">
          <Outlet />
        </main>
      </div>
    </div>
  );
};

export default PanelLayout;
